import { useEffect, useMemo, useRef, useState } from 'react'
import type { KeyboardEvent, RefObject } from 'react'
import { Search, X } from 'lucide-react'
import { cn } from '@/lib/utils'
// Relative imports (not the '@/components' barrel) to avoid a cycle now that
// the barrel exports Workbench, which renders this file.
import { Dialog, DialogContent, DialogTitle } from '../Dialog/Dialog'
import type { SymbolListItem } from '@/bot/adapters/symbolsFetcher'

// Symbol search — TradingView-style modal. Title + close, big search field with
// clear, asset-class filter pills (derived from the datafeed's `type` values),
// flat result rows (avatar · ticker · name · type). Opens from the toolbar
// trigger OR by typing on the chart (parent seeds the first char).
// Controlled: open / onOpenChange / seed (initial query) / value / onChange.

export interface WorkbenchSymbolSearchProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  symbols: SymbolListItem[]
  value: string
  onChange: (symbol: string) => void
  seed?: string
}

export function WorkbenchSymbolSearch({
  open,
  onOpenChange,
  symbols,
  value,
  onChange,
  seed = '',
}: WorkbenchSymbolSearchProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        overlayClassName="bg-black/60"
        className="v1 bg-popover max-w-[600px] sm:max-w-[600px] w-[94vw] p-0 gap-0 overflow-hidden"
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          inputRef.current?.focus()
        }}
      >
        <DialogTitle className="px-4 pt-4 pb-2 text-section text-foreground">
          Symbol search
        </DialogTitle>
        {/* Radix unmounts closed content, so the body's query/tab/highlight state
            re-initializes (from `seed`) on every open — no setState-in-effect. */}
        <SymbolSearchBody
          symbols={symbols}
          value={value}
          seed={seed}
          inputRef={inputRef}
          onPick={(symbol) => {
            onChange(symbol)
            onOpenChange(false)
          }}
        />
      </DialogContent>
    </Dialog>
  )
}

function SymbolSearchBody({
  symbols,
  value,
  seed,
  inputRef,
  onPick,
}: {
  symbols: SymbolListItem[]
  value: string
  seed: string
  inputRef: RefObject<HTMLInputElement>
  onPick: (symbol: string) => void
}) {
  const [query, setQuery] = useState(seed)
  const [tab, setTab] = useState('all')
  const [hi, setHi] = useState(0) // highlighted row index
  const listRef = useRef<HTMLDivElement | null>(null)

  const tabs = useMemo(() => {
    const types = Array.from(
      new Set(symbols.map((s) => s.type).filter((t): t is string => Boolean(t))),
    )
    return ['all', ...types]
  }, [symbols])

  const results = useMemo(() => {
    const q = query.trim().toLowerCase()
    return symbols.filter(
      (s) =>
        (tab === 'all' || s.type === tab) &&
        (!q ||
          s.symbol.toLowerCase().includes(q) ||
          (s.description ?? '').toLowerCase().includes(q)),
    )
  }, [symbols, query, tab])

  // Clamp at render time (instead of a corrective setState-in-effect).
  const hiClamped = Math.min(hi, Math.max(0, results.length - 1))

  // Keep the highlighted row scrolled into view as it moves.
  useEffect(() => {
    const node = listRef.current?.querySelector(`[data-row="${hiClamped}"]`)
    node?.scrollIntoView({ block: 'nearest' })
  }, [hiClamped])

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHi(Math.min(hiClamped + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHi(Math.max(hiClamped - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const r = results[hiClamped]
      if (r) onPick(r.symbol)
    }
  }

  return (
    <>
      {/* search field */}
      <div className="px-4 pt-2">
        <div className="flex items-center gap-2 h-11 px-3 rounded-md border border-border bg-muted/40 focus-within:border-primary transition-colors">
          <Search size={16} strokeWidth={1.75} className="text-muted-foreground shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setHi(0)
            }}
            onKeyDown={onKeyDown}
            placeholder="Search symbol or name"
            className="flex-1 bg-transparent text-body text-foreground placeholder:text-muted-foreground outline-none"
          />
          {query && (
            <button
              type="button"
              onClick={() => {
                setQuery('')
                setHi(0)
                inputRef.current?.focus()
              }}
              aria-label="Clear"
              className="inline-flex items-center justify-center h-5 w-5 rounded-full text-muted-foreground hover:bg-muted hover:text-foreground transition-colors cursor-pointer shrink-0"
            >
              <X size={13} strokeWidth={2} />
            </button>
          )}
        </div>
      </div>

      {/* asset-class filter pills */}
      <div className="flex items-center gap-1.5 px-4 py-3 overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => {
              setTab(t)
              setHi(0)
            }}
            className={cn(
              'h-7 px-3 rounded-full text-label whitespace-nowrap transition-colors cursor-pointer shrink-0',
              tab === t
                ? 'bg-foreground text-background'
                : 'bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            {t === 'all' ? 'All' : t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {/* results */}
      <div ref={listRef} className="max-h-[52dvh] overflow-y-auto border-t border-border">
        {results.length === 0 ? (
          <div className="px-4 py-10 text-center text-body text-muted-foreground">
            No symbols match &ldquo;{query}&rdquo;.
          </div>
        ) : (
          results.map((s, i) => {
            const on = s.symbol === value
            return (
              <button
                key={s.symbol}
                data-row={i}
                type="button"
                onMouseEnter={() => setHi(i)}
                onClick={() => onPick(s.symbol)}
                className={cn(
                  'w-full flex items-center gap-3 px-4 h-12 text-left transition-colors cursor-pointer border-b border-border/50 last:border-b-0',
                  i === hiClamped ? 'bg-muted/70' : on ? 'bg-muted/30' : 'hover:bg-muted/40',
                )}
              >
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-muted text-caption font-semibold text-foreground shrink-0">
                  {s.symbol.slice(0, 1)}
                </span>
                <span className="w-24 shrink-0 text-left font-semibold tabular-nums text-label text-foreground">
                  {s.symbol}
                </span>
                <span className="truncate flex-1 text-left text-body text-muted-foreground">
                  {s.description ?? ''}
                </span>
                {s.type ? (
                  <span className="text-caption text-muted-foreground/70 shrink-0">{s.type}</span>
                ) : null}
              </button>
            )
          })
        )}
      </div>
    </>
  )
}
